import LegalPageLayout from '../../components/LegalPageLayout'

export default function Disclaimer() {
  return (
    <LegalPageLayout title="Disclaimer" lastUpdated="February 6, 2026">
      <div className="space-y-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <p className="text-sm text-gray-700">
            <strong>PLACEHOLDER DOCUMENT:</strong> This document contains placeholder legal text. 
            You must have this reviewed and customized by a qualified legal professional before going live. 
            This is not legal advice.
          </p>
        </div>

        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-red-900 mb-3">⚠️ IMPORTANT DISCLAIMER</h3>
          <p className="text-red-900 font-semibold">
            LoanLoop is a comparison service only. We DO NOT provide credit, lending services, or financial advice. 
            All information is general in nature and may not be suitable for your specific circumstances.
          </p>
        </div>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Comparison Service Only</h2>
          <p className="text-gray-700 mb-4">
            LoanLoop provides information and comparison tools to help you research mortgage products. 
            We are not a credit provider, broker, or financial advisor.
          </p>
          <p className="text-gray-700 mb-4">
            <strong>We do not:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Provide credit or lending services</li>
            <li>Approve or process loan applications</li>
            <li>Provide financial advice or recommendations</li>
            <li>Act as an intermediary between you and lenders</li>
            <li>Guarantee loan approvals or rates</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Rate and Information Disclaimer</h2>
          
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 my-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Interest Rates are Indicative Only</h3>
            <p className="text-gray-700">
              All interest rates, fees, and loan terms displayed on this Website are based on publicly available 
              information at the time of publication and are <strong>INDICATIVE ONLY</strong>.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Rates May Vary</h3>
          <p className="text-gray-700 mb-4">
            The actual interest rate you receive may differ from displayed rates based on:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Your individual financial circumstances</li>
            <li>Credit history and credit score</li>
            <li>Loan-to-value ratio (LVR)</li>
            <li>Employment status and income verification</li>
            <li>Property type and location</li>
            <li>Loan amount and term</li>
            <li>Whether you are purchasing or refinancing</li>
            <li>Other lender-specific criteria</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Rates Subject to Change</h3>
          <p className="text-gray-700 mb-4">
            <strong className="text-red-600">Lenders may change rates, fees, and offers at any time without notice.</strong>
          </p>
          <p className="text-gray-700 mb-4">
            The mortgage market is dynamic. Rates displayed on our Website:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>May not reflect the most current offerings</li>
            <li>Can change daily or even multiple times per day</li>
            <li>May be withdrawn or modified by lenders without warning</li>
            <li>Are not guaranteed to be available when you apply</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.3 Verification Required</h3>
          <p className="text-gray-700">
            <strong>You must verify all rates, fees, and terms directly with lenders before making any decisions.</strong> 
            Always obtain current information from the lender's official website or by contacting them directly.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. No Guarantee of Approval</h2>
          <p className="text-gray-700 mb-4">
            Displaying a loan product on our Website does not mean:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>You will be approved for that loan</li>
            <li>The displayed rate will be offered to you</li>
            <li>The product is still available</li>
            <li>The terms shown will apply to your application</li>
          </ul>
          <p className="text-gray-700 mt-4">
            Loan approval is at the sole discretion of the lender based on their assessment criteria. 
            LoanLoop has no influence over lending decisions.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Not Financial Advice</h2>
          <p className="text-gray-700 mb-4">
            <strong>Information on this Website is general in nature and does not constitute financial advice.</strong>
          </p>
          <p className="text-gray-700 mb-4">
            We do not:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Consider your personal financial situation</li>
            <li>Assess your objectives or needs</li>
            <li>Provide recommendations tailored to you</li>
            <li>Advise whether any product is suitable for you</li>
          </ul>
          <p className="text-gray-700 mt-4">
            <strong>Before making any financial decision, you should:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li>Seek independent advice from a qualified financial advisor or mortgage broker</li>
            <li>Read all product disclosure statements and terms provided by lenders</li>
            <li>Consider whether any product is appropriate for your circumstances</li>
            <li>Verify all information independently</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Information</h2>
          <p className="text-gray-700 mb-4">
            Information about loan products is sourced from:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Lender websites and public disclosures</li>
            <li>Product disclosure statements</li>
            <li>Industry publications</li>
            <li>Third-party data providers</li>
          </ul>
          <p className="text-gray-700 mt-4">
            While we make reasonable efforts to ensure accuracy, we do not control third-party information 
            and cannot guarantee its completeness, accuracy, or timeliness.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
          <p className="text-gray-700 mb-4">
            <strong>To the maximum extent permitted by law, LoanLoop is not liable for:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Inaccuracies in displayed rates, fees, or terms</li>
            <li>Changes to lender offerings or rate adjustments</li>
            <li>Loan application outcomes (approvals or rejections)</li>
            <li>Differences between displayed rates and actual rates offered</li>
            <li>Withdrawal or modification of products by lenders</li>
            <li>Financial losses arising from decisions made based on our information</li>
            <li>Reliance on information displayed without independent verification</li>
            <li>Any direct, indirect, or consequential losses</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. No Responsibility for Lender Actions</h2>
          <p className="text-gray-700">
            LoanLoop is not responsible for:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
            <li>Lender policies, procedures, or decisions</li>
            <li>Changes to lender terms or conditions</li>
            <li>Lender customer service or communication</li>
            <li>Processing times or delays</li>
            <li>Disputes between you and lenders</li>
          </ul>
          <p className="text-gray-700 mt-4">
            Any issues with loan applications or lender services must be resolved directly with the lender.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Website Availability and Accuracy</h2>
          <p className="text-gray-700">
            We do not warrant that:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
            <li>The Website will be available at all times</li>
            <li>Information is error-free or complete</li>
            <li>Calculators will produce accurate results for your situation</li>
            <li>The Website is free from viruses or harmful components</li>
          </ul>
          <p className="text-gray-700 mt-4">
            You use this Website entirely at your own risk.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Your Responsibility</h2>
          <p className="text-gray-700 mb-4">
            <strong>It is your responsibility to:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Verify all information before making decisions</li>
            <li>Contact lenders directly for current, accurate rates and terms</li>
            <li>Seek professional financial advice</li>
            <li>Read all product documentation from lenders</li>
            <li>Understand the risks and obligations of any loan product</li>
            <li>Make informed decisions based on your circumstances</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have questions about this disclaimer:
          </p>
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <p className="text-gray-700"><strong>Email:</strong> legal@loanloop.com.au</p>
            <p className="text-gray-700"><strong>Address:</strong> [Your Business Address]</p>
          </div>
        </section>

        <div className="mt-8 p-6 bg-red-50 border-l-4 border-red-400">
          <p className="text-sm text-gray-700">
            <strong>LEGAL REVIEW REQUIRED:</strong> This document is a placeholder template only. 
            It must be reviewed, customized, and approved by a qualified legal professional before publication. 
            Ensure it provides adequate protection for your specific business model and complies with Australian consumer law.
          </p>
        </div>
      </div>
    </LegalPageLayout>
  )
}
